import { Box, Image, Link, ListItem, Text } from '@customerio/pluma-components/react';
import type { Highlight } from '@/services/billing/types';
import { buildSearchString } from '@/utils/url';
import tealBullet from '@/assets/images/billing/billing-bullet-teal.svg';

type PlanHighlightItemProps = {
  highlight: Highlight;
};

/** A route highlight's href, appending its query bag (`{ label, route, query }`) when present. */
function routeHref(route: string, query: Record<string, string> | undefined): string {
  const search = buildSearchString(query);
  return search === '' ? route : `${route}?${search}`;
}

/**
 * A single plan-highlight list item: a teal check bullet followed by the highlight, which is a plain
 * string, an external link (`{ label, href }`), or an internal route link (`{ label, route, query }`).
 * Journeys plan highlights are all plain strings today; the link/route branches are kept for the
 * shared Highlight type.
 */
export function PlanHighlightItem({ highlight }: PlanHighlightItemProps) {
  const bullet = (
    <Box mt="025" mr="100" flexShrink="0">
      <Image src={tealBullet} alt="" aria-hidden="true" />
    </Box>
  );

  if (typeof highlight === 'string') {
    return (
      <ListItem display="flex" alignItems="flex-start" data-test-plan-highlight>
        {bullet}
        <Text>{highlight}</Text>
      </ListItem>
    );
  }

  if ('href' in highlight) {
    return (
      <ListItem display="flex" alignItems="flex-start" data-test-plan-highlight>
        {bullet}
        <Link href={highlight.href}>{highlight.label}</Link>
      </ListItem>
    );
  }

  return (
    <ListItem display="flex" alignItems="flex-start" data-test-plan-highlight>
      {bullet}
      <Link href={routeHref(highlight.route, highlight.query)} variant="secondary">
        {highlight.label}
      </Link>
    </ListItem>
  );
}
